import { AmountSuperScript } from "components/design/AmountSuperScript";
import ToolTip from "components/design/toolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { useMemo } from "react";
import { Formik } from "formik";
import { object, string } from "yup";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import HourGlassTimer from "static/images/PinkHourglass.svg";
import { FileIcon } from "components/design/FileIcon";
import classNames from "classnames";
import { Merchant } from "types/Models/merchant";
import { useGetTaskMerchantsQuery } from "store/apis/taskMerchants";
import { useUpdateMerchantEmailChatMutation } from "store/apis/vendors";
import { useChannelStateContext } from "stream-chat-react";
import { CopyIcon } from "components/icons/CopyIcon";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { NOT_REQUIRED } from "constants/taskContractorFilingStates";
import { AWAITING } from "constants/merchantFormStatuses";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { useLocation } from "react-router-dom";
import { ConditionalLink } from "components/conditionalLink";

const EmailInputCell = ({
  vendorId,
  existingEmail,
}: {
  vendorId: string;
  existingEmail: string | null;
}) => {
  const { copyToClipboard } = useCopyToClipboard();

  if (existingEmail) {
    return (
      <div className="t-flex t-items-center t-gap-2">
        <span className="t-text-body-sm t-text-text-60">{existingEmail}</span>
        <button
          type="button"
          onClick={() => copyToClipboard(existingEmail)}
          className="all:unset t-cursor-pointer hover:t-opacity-70"
        >
          <CopyIcon className="t-text-purple t-size-3.5" />
        </button>
      </div>
    );
  }

  return (
    <TextInput type="email" name={vendorId} placeholder="john.d@acme.inc" />
  );
};

type VendorEmailRequestModalProps = {
  show: boolean;
  closeModal: () => void;
  title?: string;
  merchantIds: string; // comma separated merchant ids
  taskId: string;
  season: string;
  messageId?: string;
  submittedCount: number;
  merchantCount: number;
};

const getWFormStatusTag = ({
  vendor,
  groupId,
  season,
}: {
  vendor: Merchant;
  groupId: string;
  season: string;
}) => {
  if (vendor.is_w_form_required === NOT_REQUIRED) {
    return (
      <Tag tagType="transparent" icon={false} rounded size="medium">
        Not required
      </Tag>
    );
  }

  if (!vendor.w_form_document) {
    return (
      <div className="t-w-4/5">
        <Dropdown.Root>
          <div className="t-flex t-items-center">
            <Dropdown.Trigger
              asChild
              disabled
              className="t-group t-select-none !t-cursor-not-allowed t-opacity-75"
            >
              <div className="t-flex t-items-center t-justify-between t-px-3 t-py-1 t-text-body-sm t-text-text-30 t-rounded t-border t-border-solid t-border-neutral-0 t-bg-surface-lighter-grey group-data-state-open:!t-border-purple">
                <div className="t-truncate">Request/upload</div>
              </div>
            </Dropdown.Trigger>
          </div>
        </Dropdown.Root>
      </div>
    );
  }

  if (vendor.form_status === AWAITING) {
    return (
      <div className="t-w-4/5">
        <ToolTip text="Awaiting response">
          <span className="t-w-full t-flex t-gap-1 t-items-center">
            <div className="t-text-body-sm t-text-text-30">Requested</div>
            <img src={HourGlassTimer} alt="HourGlassTimer" />
          </span>
        </ToolTip>
      </div>
    );
  }

  if (vendor.w_form_document) {
    return (
      <div className="t-w-4/5">
        <div className="t-flex t-items-center t-gap-1 t-text-text-30">
          <FileIcon fileType={vendor?.w_form_document?.file_type} size="24" />
          <div className="t-text-body-sm">{vendor?.w_form_choice}</div>
        </div>
      </div>
    );
  }

  return <div className="t-text-center t-text-body-sm t-text-text-60">-</div>;
};

const get1099StatusTag = (vendor: Merchant) => {
  if (vendor.filing_status === "VOIDED") {
    return <span>Voided</span>;
  }
  if (vendor.form_1099_status === "NOT_REQUIRED") {
    return <span>Not Required</span>;
  }
  if (
    vendor.form_1099_document?.payers_document ||
    vendor.form_1099_document?.recipients_document
  ) {
    return <span>Filed</span>;
  }
  if (vendor.form_1099_status === "IN_PROGRESS") {
    return <span>In Progress</span>;
  }
  return <span>-</span>;
};

export const VendorEmailRequestModal = ({
  show,
  closeModal,
  title = "Email Address Requests",
  merchantIds,
  taskId,
  season,
  messageId,
  submittedCount,
  merchantCount,
}: VendorEmailRequestModalProps) => {
  const { channel } = useChannelStateContext();
  const entityId = channel?.data?.entity_id as string;
  const groupId = channel?.data?.group_uuid as string;

  const [updateMerchantEmailChat, { isLoading: isUpdating }] =
    useUpdateMerchantEmailChatMutation();

  const handleSubmit = async (values: Record<string, string>) => {
    if (!messageId) return;

    const emailData = Object.entries(values)
      .filter(([, email]) => email.trim() !== "")
      .map(([uuid, email]) => ({ uuid, email }));

    if (emailData.length === 0) {
      closeModal();
      return;
    }

    await updateMerchantEmailChat({
      groupId,
      message_id: messageId,
      email_data: emailData,
      entity_id: entityId,
    });

    closeModal();
  };

  const {
    data: vendorsData,
    isLoading,
    isSuccess,
  } = useGetTaskMerchantsQuery(
    {
      merchant_ids: merchantIds,
      groupId,
      entityId,
      taskId,
      season,
    },
    { refetchOnMountOrArgChange: true }
  );

  const filteredMerchants = useMemo(() => {
    if (!vendorsData?.merchants) {
      return [];
    }

    return vendorsData.merchants;
  }, [vendorsData?.merchants, merchantIds]);

  const initialValues = useMemo(() => {
    const emails: Record<string, string> = {};
    filteredMerchants.forEach((vendor) => {
      if (!vendor.email) {
        emails[vendor.uuid] = "";
      }
    });
    return emails;
  }, [filteredMerchants]);

  const validationSchema = useMemo(() => {
    const shape: Record<string, ReturnType<typeof string>> = {};
    filteredMerchants.forEach((vendor) => {
      if (!vendor.email) {
        shape[vendor.uuid] = string().email("Enter a valid email");
      }
    });
    return object(shape);
  }, [filteredMerchants]);

  const columnHelper = createColumnHelper<Merchant>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("logo", {
        header: "VENDOR",
        size: 266,
        cell: (info) => {
          const vendor = info.row.original;
          return (
            <div className="t-flex t-gap-2 t-items-center">
              <Avatar
                src={vendor.logo || ""}
                alt={vendor.name}
                variant="primary"
                size="regular"
              />
              <div>
                <span className="t-text-subtext t-text-text-60">
                  {vendor.name}
                </span>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("1099_transaction_amount", {
        header: () => <div className="t-text-right t-w-full">1099 AMOUNT</div>,
        size: 143,
        cell: (info) => {
          return (
            <div
              className="t-w-full t-flex t-justify-end t-text-subtext t-text-text-60 t-pr-5 t-group"
              onClick={() => {}}
            >
              <button
                type="button"
                className={classNames("all:unset t-text-body-sm t-ml-auto")}
              >
                <AmountSuperScript amount={Math.abs(info.getValue())} />
              </button>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "wForm",
        header: "W FORM",
        size: 217,
        cell: (info) =>
          getWFormStatusTag({ vendor: info.row.original, groupId, season }),
      }),
      columnHelper.display({
        id: "form1099",
        header: () => <div className="t-text-center t-w-full">1099</div>,
        size: 143,
        cell: (info) => (
          <div className="t-text-center">
            {get1099StatusTag(info.row.original)}
          </div>
        ),
      }),
      columnHelper.display({
        id: "emailInput",
        header: "EMAIL ADDRESS",
        size: 331,
        cell: (info) => {
          const vendor = info.row.original;
          return (
            <EmailInputCell
              vendorId={vendor.uuid}
              existingEmail={vendor.email}
            />
          );
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: filteredMerchants,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const location = useLocation();
  const showRedirectButton = !location.pathname.includes("/tax/filings");
  const areAllEmailsSubmitted = submittedCount === merchantCount;

  return (
    <Modal.Root open={show} onOpenChange={closeModal}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({ submitForm }) => (
          <Modal.Content size="xxl">
            <Modal.Header>
              <Modal.Title>
                {areAllEmailsSubmitted
                  ? "Email Address Submitted"
                  : "Email Address Requests"}
              </Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <Async.Root
                isEmpty={false}
                isLoading={isLoading}
                isSuccess={isSuccess}
              >
                <Async.Success>
                  <div className="t-flex t-flex-col t-gap-4">
                    <span className="t-text-body-sm t-text-text-30">
                      Enter email address for the following vendors
                    </span>
                    <Table.Container className="t-max-h-96 t-overflow-auto">
                      <Table.Content className="t-w-full">
                        <Table.Head>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <Table.HeadRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <Table.HeadCell
                                  key={header.id}
                                  className="t-text-subtext-sm t-uppercase"
                                  style={{ width: `${header.getSize()}px` }}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                </Table.HeadCell>
                              ))}
                            </Table.HeadRow>
                          ))}
                        </Table.Head>
                        <Table.Body>
                          {table.getRowModel().rows.map((row) => (
                            <Table.Row key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <td
                                  key={cell.id}
                                  className="t-p-2"
                                  style={{
                                    width: `${cell.column.getSize()}px`,
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              ))}
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Content>
                    </Table.Container>
                  </div>
                </Async.Success>
              </Async.Root>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              {showRedirectButton && (
                <ConditionalLink
                  to={`/tax/filings/${taskId}`}
                  role="button"
                  target="_blank"
                >
                  <Button type="button">Go to filing</Button>
                </ConditionalLink>
              )}
              {areAllEmailsSubmitted ? (
                <Button type="button" onClick={closeModal}>
                  Close
                </Button>
              ) : (
                <Button
                  customType="primary"
                  onClick={submitForm}
                  isLoading={isUpdating}
                >
                  Save
                </Button>
              )}
            </Modal.FooterButtonGroup>
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};
